<script setup lang="ts">
import { computed, ref } from "vue";
import { mapState } from "../../context/context";
import { deleteArrayItem, throttle } from "@zyzgroup/core-common";
import Textarea from "../form/Textarea.vue";
import {
  INIT,
  RATES,
  completeRate,
  type RawRate,
  type StoredRate,
  type RateShowMode
} from "./common";

// for merge ce shadow-root styles
defineOptions({
  customOptions: { Textarea },
  inheritAttrs: true
});

const props = withDefaults(
  defineProps<{
    mode?: RateShowMode;
    editable?: boolean;
    modelValue?: StoredRate;
    init?: StoredRate;
  }>(),
  {
    mode: "all"
  }
);

const emits = defineEmits<{
  (event: "throttledClick"): void;
  (event: "update:modelValue", v: StoredRate): void;
  (event: "rate", v: StoredRate): void;
}>();


const PUBLIC_IMAGE_RATE_XG = `/public/images/rate-xg`;
const level1 = `${PUBLIC_IMAGE_RATE_XG}/rate_xg_level_1.svg`;
const level2 = `${PUBLIC_IMAGE_RATE_XG}/rate_xg_level_2.svg`;
const level3 = `${PUBLIC_IMAGE_RATE_XG}/rate_xg_level_3.svg`;
const level4 = `${PUBLIC_IMAGE_RATE_XG}/rate_xg_level_4.svg`;
const level5 = `${PUBLIC_IMAGE_RATE_XG}/rate_xg_level_5.svg`;
const level1_selected = `${PUBLIC_IMAGE_RATE_XG}/rate_xg_level_1_selected.svg`;
const level2_selected = `${PUBLIC_IMAGE_RATE_XG}/rate_xg_level_2_selected.svg`;
const level3_selected = `${PUBLIC_IMAGE_RATE_XG}/rate_xg_level_3_selected.svg`;
const level4_selected = `${PUBLIC_IMAGE_RATE_XG}/rate_xg_level_4_selected.svg`;
const level5_selected = `${PUBLIC_IMAGE_RATE_XG}/rate_xg_level_5_selected.svg`;

// 获取图片
function getImage(rate: number, selected: boolean) {
  switch(rate) {
    case 1: return !selected ? level1 : level1_selected;
    case 2: return !selected ? level2 : level2_selected;
    case 3: return !selected ? level3 : level3_selected;
    case 4: return !selected ? level4 : level4_selected;
    case 5: return !selected ? level5 : level5_selected;
  }
}

const mappedState = mapState([
  "color.foregroundPattern",
  "color.foregroundBorderPattern",
  "color.fontPattern",
  "color.fontTertiaryPattern",
  "color.yellowCommon",
  "color.primaryPattern",
  "color.secondaryPattern",
  "color.panelPattern"
]);

// 当前的 rate 对应的原始数据
const rawRate = ref<RawRate>(null);

function changeRawRate(rate: number) {
  if (rawRate.value && rawRate.value.rate == rate) {
    // 只更新指针
    rawRate.value = { ...rawRate.value };
    return;
  }
  const foundRate = RATES.find((r) => r.rate == rate);
  if (foundRate && foundRate.rate > 0) {
    rawRate.value = {
      ...foundRate,
      options: foundRate.options.map((o) => ({ name: o, selected: false })),
      image: getImage(foundRate.rate, false)
    } as RawRate;
  } else {
    rawRate.value = null;
  }
}

const isControlled = computed(
  () => props.modelValue !== undefined && props.modelValue !== null
);
const inner = ref<StoredRate>({ ...INIT, ...(props.init ?? {}) });

const current = computed<StoredRate>({
  get() {
    const val = isControlled.value
      ? { ...INIT, ...(props.modelValue ?? {}) }
      : inner.value;
    completeRate(val);
    return val;
  },
  set(val) {
    val = { ...INIT, ...(val ?? {}) };
    completeRate(val);
    if (isControlled.value) {
      emits("update:modelValue", val);
    } else {
      inner.value = val;
    }
    emits("rate", val);
  }
});

// 初始化
changeRawRate(current.value.rate);

// 切换评分
const selectRate = throttle((r, e) => {
  e.stopPropagation();
  if (!props.editable) {
    return;
  }
  if (r <= 0) {
    // 未选择
    emits("throttledClick");
    return;
  }
  // 本次已选择
  current.value = {
    ...current.value,
    rate: r,
    options: []
  };
  changeRawRate(r);
});

// 切换选项
const selectOption = throttle((option, e) => {
  e.stopPropagation();
  const rawOption =
    rawRate.value && rawRate.value.options.find((o) => o.name == option);
  if (current.value.options.findIndex((o) => o == option) < 0) {
    // 添加
    current.value.options.push(option);
    if (rawOption) {
      rawOption.selected = true;
    }
  } else {
    // 删除
    deleteArrayItem(current.value.options, option);
    if (rawOption) {
      rawOption.selected = false;
    }
  }
  current.value = { ...current.value };
  // changeRawRate(current.value.rate);
});

// 输入具体描述
function inputDetail(text) {
  current.value = { ...current.value, detail: text };
}

defineExpose({});
</script>

<template>
  <div
    class="rate-xg"
    :style="{
      backgroundColor: mappedState['color.foregroundPattern'].value,
      color: mappedState['color.fontPattern'].value
    }"
  >
    <!-- 打分：已固定选择 -->
    <div
      v-if="!props.editable"
      class="rate-xg-status"
      :style="{
        color: mappedState['color.fontTertiaryPattern'].value
      }"
    >
      <div
        class="rate-xg-status-item"
        :style="{
          color: mappedState['color.yellowCommon'].value
        }"
      >
        <div class="rate-xg-status-item-image">
           <img :src="getImage(current.rate, true)"></img>
        </div>
        <div class="rate-xg-status-item-name">
          {{ current.name }}
        </div>
      </div>
    </div>
    <!-- 打分：未固定选择 -->
    <div
      v-else
      class="rate-xg-status"
      :style="{
        color: mappedState['color.fontTertiaryPattern'].value
      }"
      @click="selectRate(0, $event)"
    >
      <div
        v-for="(status, index) in RATES"
        class="rate-xg-status-item"
        :style="{
          color:
            current.rate == index + 1
              ? mappedState['color.yellowCommon'].value
              : mappedState['color.fontTertiaryPattern'].value
        }"
      >
        <div
          class="rate-xg-status-item-image"
          @click.stop="selectRate(index + 1, $event)"
        >
        <img :src="getImage(status.rate, current.rate == index + 1)"></img>
      </div>
        <div class="rate-xg-status-item-name">{{ status.name }}</div>
      </div>
    </div>
    <!-- 描述 -->
    <div
      v-if="props.mode == 'all' && props.editable && current.rate > 0"
      class="rate-xg-description"
    >
      “{{ rawRate.description }}”
    </div>
    <!-- 选项：已固定选择 -->
    <div
      v-if="
        props.mode == 'all' &&
        !props.editable &&
        current.options &&
        current.options.length
      "
      class="rate-xg-options"
      :class="current.options && current.options.length == 1 ? 'center' : ''"
    >
      <div
        v-for="(option, optionIndex) in current.options"
        class="rate-xg-option-inner"
      >
        <div
          class="rate-xg-option"
          :style="{
            color: mappedState['color.fontPattern'].value,
            backgroundColor: mappedState['color.panelPattern'].value
          }"
        >
          {{ option }}
        </div>
      </div>
    </div>
    <!-- 选项：未固定选择 -->
    <div
      v-else-if="props.mode == 'all' && props.editable && current.rate > 0"
      class="rate-xg-options"
    >
      <div
        v-for="(option, optionIndex) in rawRate?.options"
        class="rate-xg-option-inner"
        @click.stop="selectOption(option.name, $event)"
      >
        <div
          class="rate-xg-option"
          :style="
            Object.assign(
              {
                border: `1px solid ${mappedState['color.foregroundBorderPattern'].value}`
              },
              option.selected
                ? {
                    color: mappedState['color.primaryPattern'].value,
                    borderColor: mappedState['color.primaryPattern'].value,
                    backgroundColor: mappedState['color.secondaryPattern'].value
                  }
                : {}
            )
          "
        >
          {{ option.name }}
        </div>
      </div>
    </div>
    <!-- 详细：已固定选择 -->
    <div
      v-if="props.mode == 'all' && !props.editable && current.detail"
      class="rate-xg-detail-selected"
    >
      “{{ current.detail }}”
    </div>
    <!-- 详细：未固定选择 -->
    <div
      v-else-if="props.mode == 'all' && props.editable && current.rate > 0"
      class="rate-xg-detail"
    >
      <Textarea
        placeholder="请具体描述"
        :init="current.detail"
        @input="inputDetail"
      ></Textarea>
    </div>
  </div>
</template>

<style scoped>
.rate-xg {
  line-height: 1;
}
.rate-xg-status {
  font-size: 12px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}
.rate-xg-status-item {
  flex: 1;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
}
.rate-xg-status-item-image {
  box-sizing: border-box;
  width: 48px;
  height: 48px;
  /*
  width: 100%;
  aspect-ratio: 1 / 1;
  height: 0;
  padding-top: 100%;
  */
  object-fit: cover;
  cursor: pointer;
}
.rate-xg-status-item-image > img {
  display: block;
  width: 100%;
  height: 100%;
}
.rate-xg-status-item-name {
  margin-top: 5px;
}
.rate-xg-description {
  text-align: center;
  font-size: 16px;
  font-weight: bold;
  margin-top: 20px;
  padding: 0 var(--yz-padding-horizontal, 15px);
}
.rate-xg-options {
  display: flex;
  flex-wrap: wrap;
  font-size: 12px;
  margin-top: 20px;
  padding: 0 var(--yz-padding-horizontal, 15px);
}
.rate-xg-options.center {
  justify-content: center;
}
.rate-xg-option-inner {
  flex: 0 0 50%;
  box-sizing: border-box;
}
.rate-xg-options > .rate-xg-option-inner:nth-child(2n) {
  padding-left: 5px;
}
.rate-xg-options > .rate-xg-option-inner:nth-child(2n + 1) {
  padding-right: 5px;
}
.rate-xg-option {
  cursor: pointer;
  padding: 10px 0;
  text-align: center;
  margin-bottom: 10px;
  border-radius: 4px;
}
.rate-xg-detail {
  padding: 0 var(--yz-padding-horizontal, 15px);
}
.rate-xg-detail-selected {
  font-size: 14px;
  line-height: 1.4;
  text-align: center;
  margin-top: 10px;
  padding: 0 var(--yz-padding-horizontal, 15px);
}
</style>
